import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { SORT_TYPES, buildSalesTaxColumns, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/salesTaxColumnsConfig';
import { buildSalesTaxFilterGroups } from '../constants/salesTaxFiltersConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { ToastContainer } from 'react-toastify';
import { toastError } from '../../../shared/toastHelper';
import LedgerLineItemsExportButton from './LedgerLineItemsExportButton';
import {
  SALES_TAX_REPORT, LEDGER_SCOPE_FILTER_IDS, salesTaxRowSelection, ledgerLineItemsHref, ledgerScopeParams,
} from '../utils/ledgerLineItems';

const axios = require('axios').default;

const SALES_TAX_CONFIG = {
  pageTitle: 'Sales Tax',
  pageSubtitle: 'Sales tax collected on tickets, add-ons, and passes sold through Opendate. Refunds post as negative amounts on the day they were issued. Imported sales appear only where tax was recorded at import. Days are counted in each venue\'s time zone.',
  sidebarKey: 'finances_sales_tax',
  defaultPreset: 'year_to_date',
  dateModeOptions: [{ key: 'created_at', label: 'Collected Date' }],
  urlFilterIds: LEDGER_SCOPE_FILTER_IDS,

  sortTypes: SORT_TYPES,
  localStorageKey: (teamId) => `reportingFinancesSalesTaxColumns_${teamId}`,
  firstColumnId: 'tax_name',
  defaultSortRules: [{ field: 'tax_name', direction: 'asc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/sales-tax/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/sales-tax/data.json`,
  detailResponseKey: 'rows',
  detailErrorMessage: 'Failed to load sales tax data.',
  detailTableTitle: 'Sales Tax',
  emptyMessage: 'No sales tax collected in this range.',
  searchPlaceholder: 'Search taxes...',
  paginationLabel: 'taxes',
  itemLabel: (count) => `tax${count !== 1 ? 'es' : ''}`,

  buildKpiCards: (current, prior) => [
    {
      label: 'Collected',
      value: compactCurrency(current.collected || 0),
      current: current.collected || 0,
      prior: (prior && prior.collected) || 0,
    },
  ],
  kpiGridColumns: 1,

  overTimeKey: null,

  topCharts: [],

  groupByOptions: [],
  groupByDefault: null,

  renderCellValue: (col, row, {
    currencyFormatter: cf, team, dateRange, activeFilters,
  }) => {
    switch (col.id) {
      case 'tax_name': return row.tax_name || '—';
      case 'rate': return row.rate != null ? `${parseFloat(row.rate)}%` : '—';
      case 'general_ledger_code': return row.general_ledger_code || '—';
      case 'collected': {
        if (!team?.can_access_general_ledger_codes) return cf.format(row.collected || 0);
        return (
          <a
            href={ledgerLineItemsHref(team.id, SALES_TAX_REPORT, { ...ledgerScopeParams(dateRange, activeFilters), ...salesTaxRowSelection(row) })}
            title="View line items"
            style={{ color: 'var(--sl-fg-brand, #1982c4)' }}
          >
            {cf.format(row.collected || 0)}
          </a>
        );
      }
      default: return '—';
    }
  },

  filterTitle: 'Filter Sales Tax',
  filterSubtitle: 'Apply filters to narrow sales tax data.',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.tax_name && activeFilters.tax_name.length > 0) {
    params.tax_name = activeFilters.tax_name;
  }
  if (activeFilters.general_ledger_code && activeFilters.general_ledger_code.length > 0) {
    params.general_ledger_code = activeFilters.general_ledger_code;
  }
  LEDGER_SCOPE_FILTER_IDS.forEach((id) => {
    if (activeFilters[id]?.length) params[id] = activeFilters[id];
  });
  return params;
};

const salesTaxLineItemParams = (dateRange, activeFilters) => {
  const params = { ...ledgerScopeParams(dateRange, activeFilters), source: ['Tax'] };
  if (activeFilters.tax_name?.length) params.tax_name = activeFilters.tax_name;
  if (activeFilters.general_ledger_code?.length) params.general_ledger_code = activeFilters.general_ledger_code;
  return params;
};

function ReportingFinancesSalesTaxPage({ team, csrfToken, venueOwnerships, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return undefined;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    let ignore = false;
    axios.get(`/teams/${team.id}/reporting/finances/sales-tax/filter_options.json`)
      .then((res) => { if (!ignore) setFilterOptions(res.data || {}); })
      .catch(() => {
        if (!ignore) toastError('Could not load the sales tax filters. Reload the page to try again.');
      });
    return () => { ignore = true; };
  }, [team?.id, csrfToken]);

  const generalLedgerCodesEnabled = Boolean(team?.can_access_general_ledger_codes);

  const configWithFilters = {
    ...SALES_TAX_CONFIG,
    columns: buildSalesTaxColumns(generalLedgerCodesEnabled),
    getDefaultVisibleColumns: () => getDefaultVisibleColumns(generalLedgerCodesEnabled),
    getDefaultHiddenColumns: () => getDefaultHiddenColumns(generalLedgerCodesEnabled),
    buildFilterGroups: () => buildSalesTaxFilterGroups(filterOptions, venueOwnerships, generalLedgerCodesEnabled),
    buildDetailApiFilters,
    renderToolbarExtras: generalLedgerCodesEnabled
      ? ({ dateRange, activeFilters }) => (
        <LedgerLineItemsExportButton team={team} csrfToken={csrfToken} params={salesTaxLineItemParams(dateRange, activeFilters)} />
      )
      : undefined,
  };
  return (
    <>
      <SalesDetailPage
        {...props}
        team={team}
        csrfToken={csrfToken}
        venueOwnerships={venueOwnerships}
        config={configWithFilters}
      />
      <ToastContainer />
    </>
  );
}

export default ReportingFinancesSalesTaxPage;
